import {useEffect, useMemo, useRef, useState} from "react";
import {isValid, parse} from "date-fns";
import * as Slider from "@radix-ui/react-slider";
import {Card} from "../../../../../../components/card/card.jsx";
import {useGarrisonTimelineGridStore} from "../../../hooks/use-garrison-timeline-grid-store.js";

const GarrisonTimelineSlider = ({filter, zoom = {start: 0, end: 100}, setZoom, isTimeline, showSeconds = false, elapsedSeconds = 0, setElapsedSeconds = () => {}, isCropMode, cropRange, setCropRange}) => {
  const {setActiveMarkerKey} = useGarrisonTimelineGridStore();
  const {baseStart, startDate, endDate, visibleTotalSeconds, rulerMarks, visibleStartSeconds} = useMemo(() => {
    const parseFilterDate = (date) => {
      if (!date) return new Date();

      const referenceDate = new Date();
      referenceDate.setHours(0, 0, 0, 0);

      const parsedDateWithSeconds = parse(date, "dd/MM/yyyy HH:mm:ss", referenceDate);
      if (isValid(parsedDateWithSeconds)) return parsedDateWithSeconds;

      const parsedDateWithoutSeconds = parse(date, "dd/MM/yyyy HH:mm", referenceDate);
      if (isValid(parsedDateWithoutSeconds)) return parsedDateWithoutSeconds;

      const fallbackDate = new Date(date);
      return isValid(fallbackDate) ? fallbackDate : new Date();
    };

    const dtStart = filter?.dtStart || new Date().toISOString();
    const baseStartObj = parseFilterDate(dtStart);

    const dtEnd = filter?.dtEnd;
    const baseEndObj = dtEnd ? parseFilterDate(dtEnd) : new Date(baseStartObj.getTime() + 86400000);

    const baseTotal = Math.max(0, Math.floor((baseEndObj.getTime() - baseStartObj.getTime()) / 1000));

    const vStartSecs = Math.floor((baseTotal * zoom.start) / 100);
    const vEndSecs = Math.floor((baseTotal * zoom.end) / 100);
    const vTotal = Math.max(1, vEndSecs - vStartSecs);

    const startObj = new Date(baseStartObj.getTime() + vStartSecs * 1000);
    const endObj = new Date(baseStartObj.getTime() + vEndSecs * 1000);

    const steps = 8;
    const marks = Array.from({length: steps + 1}, (_, i) => Math.floor((vTotal / steps) * i));

    return {
      baseStart: baseStartObj,
      startDate: startObj,
      endDate: endObj,
      visibleTotalSeconds: vTotal,
      rulerMarks: marks,
      visibleStartSeconds: vStartSecs
    };
  }, [filter, zoom]);

  const [dynamicLineHeight, setDynamicLineHeight] = useState(0);
  const sliderRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverSeconds, setHoverSeconds] = useState(0);
  const [isDraggingCrop, setIsDraggingCrop] = useState(false);
  const [isDraggingPan, setIsDraggingPan] = useState(false);
  const dragStartRef = useRef(null);
  const cropHasMovedRef = useRef(false);
  const panStartX = useRef(0);
  const panStartZoom = useRef({start: 0, end: 100});
  const panHasMoved = useRef(false);
  const panAnimationFrameRef = useRef(null);
  const pendingPanZoomRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const secondsAtPosition = visibleStartSeconds + (percentage * visibleTotalSeconds);

    setHoverSeconds(currentSeconds => currentSeconds === secondsAtPosition ? currentSeconds : secondsAtPosition);
  };

  const handleWheelZoom = (e) => {
    if (!setZoom || !isTimeline || e.deltaY === 0) return;

    e.preventDefault();

    const rect = e.currentTarget.getBoundingClientRect();
    const pointerRatio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const currentZoomRange = zoom.end - zoom.start;
    const nextZoomRange = Math.min(100, Math.max(5, currentZoomRange * (e.deltaY > 0 ? 1.1 : 0.9)));
    const anchor = zoom.start + currentZoomRange * pointerRatio;
    const nextZoomStart = Math.min(Math.max(anchor - nextZoomRange * pointerRatio, 0), 100 - nextZoomRange);

    setZoom(currentZoom => currentZoom.start === nextZoomStart && currentZoom.end === nextZoomStart + nextZoomRange
      ? currentZoom
      : {start: nextZoomStart, end: nextZoomStart + nextZoomRange});
  };

  useEffect(() => {
    const applyPanZoom = () => {
      const nextZoom = pendingPanZoomRef.current;
      panAnimationFrameRef.current = null;

      if (!nextZoom || !setZoom) return;

      setZoom(currentZoom => currentZoom.start === nextZoom.start && currentZoom.end === nextZoom.end ? currentZoom : nextZoom);
    };

    const handleGlobalPointerMove = (e) => {
      if (!isDraggingPan || !sliderRef.current) return;

      const deltaX = e.clientX - panStartX.current;

      if (Math.abs(deltaX) > 3) {
        panHasMoved.current = true;
      }

      if (!panHasMoved.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const deltaPercentage = (deltaX / rect.width) * 100;
      const invertedDelta = -deltaPercentage;
      let newStart = panStartZoom.current.start + invertedDelta;
      let newEnd = panStartZoom.current.end + invertedDelta;

      if (newStart < 0) {
        newEnd -= newStart;
        newStart = 0;
      }

      if (newEnd > 100) {
        newStart -= newEnd - 100;
        newEnd = 100;
      }

      pendingPanZoomRef.current = {
        start: Math.max(0, newStart),
        end: Math.min(100, newEnd)
      };

      if (!panAnimationFrameRef.current) {
        panAnimationFrameRef.current = requestAnimationFrame(applyPanZoom);
      }
    };

    const handleGlobalPointerUp = (e) => {
      if (panAnimationFrameRef.current) {
        cancelAnimationFrame(panAnimationFrameRef.current);
        applyPanZoom();
      }

      if (isDraggingPan) {
        if (!panHasMoved.current && sliderRef.current) {
          const rect = sliderRef.current.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const percentage = Math.max(0, Math.min(1, clickX / rect.width));
          const rawSeconds = visibleStartSeconds + (percentage * visibleTotalSeconds);
          const finalSeconds = isTimeline ? rawSeconds : Math.floor(rawSeconds / 60) * 60;

          setElapsedSeconds(currentSeconds => currentSeconds === finalSeconds ? currentSeconds : finalSeconds);
          if (setActiveMarkerKey) setActiveMarkerKey(null);
        }

        setIsDraggingPan(false);
      }
    };

    if (isDraggingPan) {
      window.addEventListener("pointermove", handleGlobalPointerMove);
      window.addEventListener("pointerup", handleGlobalPointerUp);
    }

    return () => {
      window.removeEventListener("pointermove", handleGlobalPointerMove);
      window.removeEventListener("pointerup", handleGlobalPointerUp);

      if (panAnimationFrameRef.current) {
        cancelAnimationFrame(panAnimationFrameRef.current);
        panAnimationFrameRef.current = null;
      }
    };
  }, [isDraggingPan, setZoom, isTimeline, visibleStartSeconds, visibleTotalSeconds, setElapsedSeconds, setActiveMarkerKey]);

  useEffect(() => {
    const updateHeight = () => {
      if (!isTimeline) return;

      const topAnchor = document.getElementById("timeline-top-anchor");
      const dashedLine = document.getElementById("slider-dashed-line");

      if (topAnchor && dashedLine) {
        const topRect = topAnchor.getBoundingClientRect();
        const lineRect = dashedLine.getBoundingClientRect();
        setDynamicLineHeight(currentHeight => {
          const nextHeight = Math.max(0, lineRect.bottom - topRect.top - 1);
          return currentHeight === nextHeight ? currentHeight : nextHeight;
        });
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    const timeoutId = setTimeout(updateHeight, 150);

    return () => {
      window.removeEventListener("resize", updateHeight);
      clearTimeout(timeoutId);
    };
  }, [isTimeline, zoom, filter]);

  useEffect(() => {
    const visibleEndSeconds = visibleStartSeconds + visibleTotalSeconds;

    if (elapsedSeconds < visibleStartSeconds) {
      setElapsedSeconds(visibleStartSeconds);
    } else if (elapsedSeconds > visibleEndSeconds) {
      setElapsedSeconds(visibleEndSeconds);
    }
  }, [visibleStartSeconds, visibleTotalSeconds, elapsedSeconds, setElapsedSeconds]);

  const getAbsoluteDate = (seconds) => new Date(baseStart.getTime() + seconds * 1000);
  const getVisibleDate = (seconds) => new Date(startDate.getTime() + seconds * 1000);

  const formatTimeOnly = (dateObj) => {
    const h = dateObj.getHours().toString().padStart(2, "0");
    const m = dateObj.getMinutes().toString().padStart(2, "0");
    return showSeconds ? `${h}:${m}:${dateObj.getSeconds().toString().padStart(2, "0")}` : `${h}:${m}`;
  };

  const formatDateFull = (dateObj) => {
    if(!dateObj) return "";

    const d = dateObj.getDate().toString().padStart(2, "0");
    const mo = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    const y = dateObj.getFullYear();

    return `${d}/${mo}/${y}, ${formatTimeOnly(dateObj)}`;
  };

  const cropStartPct = cropRange && visibleTotalSeconds > 0 ? Math.max(0, Math.min(100, ((cropRange[0] - visibleStartSeconds) / visibleTotalSeconds) * 100)) : 0;
  const cropEndPct = cropRange && visibleTotalSeconds > 0 ? Math.max(0, Math.min(100, ((cropRange[1] - visibleStartSeconds) / visibleTotalSeconds) * 100)) : 0;
  const cropWidthPct = cropEndPct - cropStartPct;

  return (
    <Card.Body className="flex flex-col justify-center h-full px-6 relative w-full">
      <div className="text-center text-[11px] text-gray-600 mb-3">
        {startDate && endDate && (
          <span className="z-100">Período em exibição: {formatDateFull(startDate)} à {formatDateFull(endDate)}</span>
        )}
      </div>

      <div className="relative w-full z-10">
        <Slider.Root
          ref={sliderRef}
          id="slider-track-anchor"
          className={`w-full h-6 min-h-0 flex items-center relative ${isTimeline ? (isDraggingPan ? "cursor-grabbing" : "cursor-grab") : "cursor-pointer"}`}
          value={[Math.max(0, elapsedSeconds - visibleStartSeconds)]}
          max={visibleTotalSeconds > 0 ? visibleTotalSeconds : 100}
          step={isTimeline ? 1 : 60}
          onPointerDownCapture={(e) => {
            if (!isTimeline) return;
            e.preventDefault();
            e.stopPropagation();
            setIsDraggingPan(true);
            panStartX.current = e.clientX;
            panStartZoom.current = {...zoom};
            panHasMoved.current = false;
          }}
          onValueChange={([value]) => {
            if (isTimeline) return;
            const rawSeconds = value + visibleStartSeconds;
            const finalSeconds = Math.floor(rawSeconds / 60) * 60;

            setElapsedSeconds(currentSeconds => currentSeconds === finalSeconds ? currentSeconds : finalSeconds);
            if (setActiveMarkerKey) setActiveMarkerKey(null);
          }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setIsHovering(false)}
          onWheel={handleWheelZoom}
        >
          <Slider.Track className="w-full h-5 rounded-full bg-[#3399ff] relative z-0">
            <Slider.Range className="h-full rounded-full pointer-events-none" />
          </Slider.Track>

          <Slider.Thumb
            aria-label="Controle de tempo"
            className="w-1.5 h-10 opacity-0 outline-none relative z-10 pointer-events-none"
          />
        </Slider.Root>

        {isCropMode && cropRange && (
          <>
            <div
              className={`absolute top-0 h-6 z-20 pointer-events-auto ${isDraggingCrop ? 'cursor-move' : 'cursor-move'}`}
              style={{ left: `${cropStartPct}%`, width: `${cropWidthPct}%` }}
              onPointerDown={(e) => {
                e.stopPropagation();
                cropHasMovedRef.current = false;
                dragStartRef.current = {
                  x: e.clientX,
                  start: cropRange[0],
                  end: cropRange[1]
                };
                e.currentTarget.setPointerCapture(e.pointerId);
              }}
              onPointerMove={(e) => {
                if (!dragStartRef.current || !sliderRef.current) return;

                const rect = sliderRef.current.getBoundingClientRect();
                const deltaX = e.clientX - dragStartRef.current.x;

                if (!cropHasMovedRef.current && Math.abs(deltaX) <= 3) return;

                cropHasMovedRef.current = true;
                setIsDraggingCrop(true);
                const deltaPct = deltaX / rect.width;
                const deltaSecs = deltaPct * visibleTotalSeconds;

                let newStart = dragStartRef.current.start + deltaSecs;
                let newEnd = dragStartRef.current.end + deltaSecs;
                const visibleEndSeconds = visibleStartSeconds + visibleTotalSeconds;

                if (newStart < visibleStartSeconds) {
                  newStart = visibleStartSeconds;
                  newEnd = newStart + (dragStartRef.current.end - dragStartRef.current.start);
                }

                if (newEnd > visibleEndSeconds) {
                  newEnd = visibleEndSeconds;
                  newStart = newEnd - (dragStartRef.current.end - dragStartRef.current.start);
                }

                setCropRange([newStart, newEnd]);
              }}
              onPointerUp={(e) => {
                if (!cropHasMovedRef.current && sliderRef.current) {
                  const rect = sliderRef.current.getBoundingClientRect();
                  const clickX = e.clientX - rect.left;
                  const percentage = Math.max(0, Math.min(1, clickX / rect.width));
                  const nextElapsedSeconds = visibleStartSeconds + (percentage * visibleTotalSeconds);

                  setElapsedSeconds(currentSeconds => currentSeconds === nextElapsedSeconds ? currentSeconds : nextElapsedSeconds);
                  if (setActiveMarkerKey) setActiveMarkerKey(null);
                }

                setIsDraggingCrop(false);
                dragStartRef.current = null;
                cropHasMovedRef.current = false;
                e.currentTarget.releasePointerCapture(e.pointerId);
              }}
            />

            <Slider.Root
            className="absolute inset-0 flex items-center w-full h-6 pointer-events-none z-20"
            value={[
              Math.max(0, cropRange[0] - visibleStartSeconds),
              Math.max(0, cropRange[1] - visibleStartSeconds)
            ]}
            max={visibleTotalSeconds > 0 ? visibleTotalSeconds : 100}
            step={isTimeline ? 1 : 60}
            onValueChange={([val1, val2]) => {
              setCropRange([val1 + visibleStartSeconds, val2 + visibleStartSeconds]);
            }}
          >
            <Slider.Track className="relative grow h-full rounded-full">
              <Slider.Range className="absolute h-full rounded-full bg-[#fbbf24] opacity-40 pointer-events-none" />
            </Slider.Track>

            <Slider.Thumb className="block w-1.5 h-8 bg-[#facc15] cursor-ew-resize outline-none relative flex flex-col items-center justify-center pointer-events-auto z-30">
              <div className="absolute top-full bg-[#063a6c] text-white text-[11px] font-medium px-2 py-0.5 rounded shadow whitespace-nowrap">
                {formatTimeOnly(getAbsoluteDate(cropRange[0]))}
              </div>
            </Slider.Thumb>

            <Slider.Thumb className="block w-1.5 h-8 bg-[#facc15] cursor-ew-resize outline-none relative flex flex-col items-center justify-center pointer-events-auto z-30">
              <div className="absolute top-full bg-[#063a6c] text-white text-[11px] font-medium px-2 py-0.5 rounded shadow whitespace-nowrap">
                {formatTimeOnly(getAbsoluteDate(cropRange[1]))}
              </div>
            </Slider.Thumb>
            </Slider.Root>
          </>
        )}

        <div
          className="absolute top-[calc(50%+4px)] -translate-y-1/2 w-1.5 h-9 bg-[#063a6c] z-40 pointer-events-none transform -translate-x-1/2 flex flex-col items-center justify-center"
          style={{
            left: `${visibleTotalSeconds > 0 ? Math.max(0, Math.min(100, ((elapsedSeconds - visibleStartSeconds) / visibleTotalSeconds) * 100)) : 0}%`
          }}
        >
          <div className="absolute top-full z-50 bg-[#063a6c] text-white text-[11px] font-medium px-2 py-0.5 rounded shadow whitespace-nowrap">
            {formatTimeOnly(getAbsoluteDate(elapsedSeconds))}
          </div>
          { isTimeline &&
            <div
              id="slider-dashed-line"
              className="absolute bottom-full w-px border-l border-dashed pointer-events-none z-50 "
              style={{ height: dynamicLineHeight > 0 ? `${dynamicLineHeight}px` : "0px" }}
            />
          }
        </div>

        {isHovering && !isDraggingPan && (
          <div
            className="absolute top-[calc(50%+4px)] -translate-y-1/2 w-1.5 h-9 bg-[#063a6c]/50 z-50 pointer-events-none transform -translate-x-1/2 flex flex-col items-center justify-center"
            style={{
              left: `${visibleTotalSeconds > 0 ? Math.max(0, Math.min(100, ((hoverSeconds - visibleStartSeconds) / visibleTotalSeconds) * 100)) : 0}%`
            }}
          >
            <div className="absolute top-full z-50 bg-[#063a6c]/80 text-white text-[11px] font-medium px-2 py-0.5 rounded shadow whitespace-nowrap pointer-events-none">
              {formatTimeOnly(getAbsoluteDate(hoverSeconds))}
            </div>
            {isTimeline && (
              <div
                id="slider-dashed-line-hover"
                className="absolute bottom-full w-px border-l border-dashed border-[#063a6c]/50 pointer-events-none z-30"
                style={{height: dynamicLineHeight > 0 ? `${dynamicLineHeight}px` : "0px"}}
              />
            )}
          </div>
        )}
      </div>

      <div className="relative w-full h-8 text-[11px] text-gray-500 z-0 pointer-events-none mt-1">
        {rulerMarks.map((mark, index) => {
          const percent = visibleTotalSeconds > 0 ? (mark / visibleTotalSeconds) * 100 : 0;

          return (
            <div
              key={index}
              className="absolute top-0 -translate-x-1/2 flex flex-col items-center"
              style={{ left: `${percent}%` }}
            >
              <div className="w-px h-1.5 bg-gray-300 mb-0.5" />
              <span>{formatTimeOnly(getVisibleDate(mark))}</span>
            </div>
          );
        })}
      </div>
    </Card.Body>
  );
};

export default GarrisonTimelineSlider;
